import { MutableRefObject, useCallback, useRef } from "react";
import { ShareLinkFile } from "../entities";
import { FileSelection } from "./useFileSelection";

export type FileRangeSelection = {
  /**
   * Uid a Shift range extends from — the last item clicked without Shift. Null
   * when nothing has been clicked yet.
   *
   * Held as a uid, not a display index: the order underneath it changes (a sort
   * flip, a grid↔list switch, a folder navigation), and an index would then name
   * a different file than the one the user actually clicked.
   */
  anchorRef: MutableRefObject<string | null>;
  /** Uid of the moving end of the range (the keyboard arrow cursor). */
  cursorRef: MutableRefObject<string | null>;
  /** Cmd/Ctrl+click (and the checkbox): add/remove this file, keeping the rest. */
  toggle: (uid: string) => void;
  /** Plain click: the selection becomes exactly this file. */
  selectExclusive: (uid: string) => void;
  /** Shift+click: the selection becomes the anchor → this file range. */
  selectRange: (uid: string) => void;
  /** Drop the whole selection and forget the anchor. */
  clear: () => void;
};

type FileRangeSelectionOptions = {
  /** The files in display (sorted) order — ranges are slices of this. */
  files: ShareLinkFile[];
  /** The selection state the handlers mutate. */
  selection: FileSelection;
  /**
   * Ran before every selection change, for state that lives beside the file
   * selection (the published space clears its folder highlight here).
   */
  onInteract?: () => void;
};

/**
 * Desktop-file-manager selection gestures on top of {@link useFileSelection}:
 * a plain click moves the selection, Cmd/Ctrl+click grows it item by item, and
 * Shift+click takes the contiguous range from the anchor (the last plainly
 * clicked file) — which stays put, so the range can be re-extended.
 *
 * Wire the returned handlers to a card/row's `onSelectExclusive`,
 * `onSelectRange` and `onToggleSelect` (see `ItemActivation`). The anchor and
 * cursor are exposed as refs so a keyboard handler can extend the same range
 * with Shift+arrow.
 *
 * Every handler is referentially stable (the live files/selection are read
 * through a ref), so passing them to memoized cards never re-renders the grid.
 */
export const useFileRangeSelection = ({
  files,
  selection,
  onInteract,
}: FileRangeSelectionOptions): FileRangeSelection => {
  const anchorRef = useRef<string | null>(null);
  const cursorRef = useRef<string | null>(null);

  // Latest render's inputs, so the handlers below can stay stable.
  const latest = useRef({ files, selection, onInteract });
  latest.current = { files, selection, onInteract };

  /** Pins the anchor (and cursor) to a file, and runs the interaction hook. */
  const anchorAt = useCallback((uid: string) => {
    anchorRef.current = uid;
    cursorRef.current = uid;
    latest.current.onInteract?.();
  }, []);

  const toggle = useCallback(
    (uid: string) => {
      anchorAt(uid);
      latest.current.selection.toggle(uid);
    },
    [anchorAt]
  );

  const selectExclusive = useCallback(
    (uid: string) => {
      anchorAt(uid);
      latest.current.selection.setSelection(new Set([uid]));
    },
    [anchorAt]
  );

  const selectRange = useCallback((uid: string) => {
    const { files, selection, onInteract } = latest.current;
    const index = files.findIndex((file) => file.uid === uid);
    if (index < 0) return;
    // Without a previous click, or with an anchor whose file has since left the
    // listing, the range is just the clicked file.
    const anchorIndex = files.findIndex(
      (file) => file.uid === anchorRef.current
    );
    const anchor = anchorIndex >= 0 ? anchorIndex : index;
    const lo = Math.min(anchor, index);
    const hi = Math.max(anchor, index);
    onInteract?.();
    selection.setSelection(
      new Set(files.slice(lo, hi + 1).map((file) => file.uid))
    );
    // The anchor outlives the click so the range can be re-extended from it.
    anchorRef.current = files[anchor].uid;
    cursorRef.current = uid;
  }, []);

  const clear = useCallback(() => {
    anchorRef.current = null;
    cursorRef.current = null;
    latest.current.onInteract?.();
    // `clear` (not `setSelection(new Set())`) so clearing an already-empty
    // selection stays a no-op instead of forcing a render.
    latest.current.selection.clear();
  }, []);

  return { anchorRef, cursorRef, toggle, selectExclusive, selectRange, clear };
};
